"use client";

import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  AlertTriangle,
  Loader2,
  Mail,
  MapPin,
  Phone,
  Send,
  ShieldCheck,
  User,
  X,
  Calendar,
  Clock,
  Users,
  RefreshCw,
} from "lucide-react";
import { postReservaTaxi } from "../lib/api/taxi/postReservaTaxi";
import {
  formatearPrecio,
  type ReservaCreada,
  type ReservaTaxi,
  type ReservaTaxiPendiente,
  type TipoVehiculo,
} from "../lib/api/taxi/types";
import { ToastContainer } from "./Toast";
import { useToast } from "../hooks/useToast";

interface TaxiReservaModalProps {
  isOpen: boolean;
  onClose: () => void;
  reservaData: ReservaTaxiPendiente;
  /** Destino y precio estimado sólo para mostrar; el precio bueno lo devuelve
   *  el servidor al crear la reserva. */
  resumen: { destino: string; precio: number };
  tipoVehiculo: TipoVehiculo;
}

const RESEND_COOLDOWN_SECONDS = 60;

export default function TaxiReservaModal({
  isOpen,
  onClose,
  reservaData,
  resumen,
  tipoVehiculo,
}: TaxiReservaModalProps) {
  const [nombre, setNombre] = useState("");
  const [correo, setCorreo] = useState("");
  const [telefono, setTelefono] = useState("");
  const [aceptaTerminos, setAceptaTerminos] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reserva, setReserva] = useState<ReservaCreada | null>(null);
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const { toasts, showToast, closeToast } = useToast();

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  useEffect(() => {
    if (!isOpen) {
      setNombre("");
      setCorreo("");
      setTelefono("");
      setAceptaTerminos(false);
      setReserva(null);
      setLoading(false);
      setResending(false);
      setResendCooldown(0);
    }
  }, [isOpen]);

  const validarCorreo = (email: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!nombre.trim()) {
        showToast(
          "warning",
          "Falta tu nombre",
          "Ingresa tu nombre completo para continuar.",
        );
        return;
      }
      if (!correo.trim() || !validarCorreo(correo)) {
        showToast(
          "warning",
          "Correo inválido",
          "Ingresa un correo electrónico válido.",
        );
        return;
      }
      if (!telefono.trim() || telefono.replace(/\D/g, "").length < 7) {
        showToast(
          "warning",
          "Teléfono inválido",
          "Ingresa un número de teléfono válido.",
        );
        return;
      }
      if (!aceptaTerminos) {
        showToast(
          "warning",
          "Acepta los términos",
          "Debes aceptar el aviso legal antes de reservar.",
        );
        return;
      }

      setLoading(true);
      const fullReservaData: ReservaTaxi = {
        ...reservaData,
        nombre: nombre.trim(),
        correo: correo.trim(),
        telefono: telefono.trim(),
        tipo_vehiculo: tipoVehiculo,
      };

      const result = await postReservaTaxi(fullReservaData);
      setLoading(false);

      if (result.success) {
        setReserva(result.data);
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
        showToast(
          "success",
          "¡Reserva enviada!",
          "Revisa y confirma tu correo electrónico.",
        );
      } else {
        showToast(
          "error",
          "No pudimos procesar tu reserva",
          result.message || "Intenta nuevamente en unos minutos.",
        );
      }
    },
    [
      nombre,
      correo,
      telefono,
      aceptaTerminos,
      reservaData,
      tipoVehiculo,
      showToast,
    ],
  );

  const handleResend = useCallback(async () => {
    if (resendCooldown > 0 || resending) return;
    setResending(true);

    const fullReservaData: ReservaTaxi = {
      ...reservaData,
      nombre: nombre.trim(),
      correo: correo.trim(),
      telefono: telefono.trim(),
      tipo_vehiculo: tipoVehiculo,
    };

    const result = await postReservaTaxi(fullReservaData);
    setResending(false);

    if (result.success) {
      setReserva(result.data);
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      showToast(
        "success",
        "Correo reenviado",
        "Revisa tu bandeja de entrada nuevamente.",
      );
    } else {
      showToast(
        "error",
        "No pudimos reenviar el correo",
        result.message || "Intenta nuevamente en unos momentos.",
      );
    }
  }, [
    resendCooldown,
    resending,
    reservaData,
    nombre,
    correo,
    telefono,
    tipoVehiculo,
    showToast,
  ]);

  const formatearFechaResumen = (fechaStr: string) => {
    if (!fechaStr) return fechaStr;
    try {
      const fecha = new Date(fechaStr + "T00:00:00");
      return fecha.toLocaleDateString("es-ES", {
        weekday: "short",
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    } catch {
      return fechaStr;
    }
  };

  return (
    <>
      <ToastContainer toasts={toasts} onClose={closeToast} />
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-[9998] bg-slate-950/60 backdrop-blur-sm"
              onClick={!loading ? onClose : undefined}
            />

            <div className="fixed inset-0 z-[9999] flex items-start sm:items-center justify-center p-4 overflow-y-auto overscroll-contain">
              <motion.div
                initial={{ opacity: 0, y: 30, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 30, scale: 0.96 }}
                transition={{ type: "spring", stiffness: 280, damping: 28 }}
                className="bg-white rounded-3xl shadow-2xl w-full max-w-lg my-4 sm:my-8 overflow-hidden flex flex-col max-h-[calc(100vh-2rem)] sm:max-h-[90vh]"
              >
                {/* Header con gradiente */}
                <div className="relative bg-gradient-to-br from-[#102542] via-[#1a3456] to-[#243d6a] px-6 pt-6 pb-8 shrink-0">
                  <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/5 rounded-full blur-3xl" />
                  <div className="absolute -bottom-12 -left-12 w-48 h-48 bg-cyan-300/10 rounded-full blur-3xl" />

                  <div className="relative flex items-start justify-between">
                    <div>
                      <p className="text-xs uppercase tracking-widest text-cyan-200/80 font-semibold">
                        {reserva ? "Reserva enviada" : "Completa tu reserva"}
                      </p>
                      <h2 className="text-2xl font-bold text-white mt-1">
                        {reserva ? "¡Casi listo!" : "Solicitud de Taxi"}
                      </h2>
                      <p className="text-sm text-white/70 mt-1">
                        {reserva
                          ? "Confirma desde tu correo para hacerla efectiva"
                          : "Solo unos datos para confirmar tu viaje"}
                      </p>
                    </div>
                    <button
                      onClick={onClose}
                      disabled={loading}
                      className="text-white/70 hover:text-white transition-colors p-1.5 rounded-full hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Contenido */}
                <div className="p-6 md:p-8 overflow-y-auto flex-1">
                  <AnimatePresence mode="wait">
                    {reserva ? (
                      <motion.div
                        key="success"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="flex flex-col items-center text-center"
                      >
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{
                            type: "spring",
                            stiffness: 260,
                            damping: 18,
                            delay: 0.1,
                          }}
                          className="relative w-20 h-20 mb-5"
                        >
                          <div className="absolute inset-0 bg-emerald-100 rounded-full animate-ping opacity-50" />
                          <div className="relative w-20 h-20 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-200">
                            <Mail className="w-10 h-10 text-white" />
                          </div>
                        </motion.div>

                        <h3 className="text-xl font-bold text-slate-800">
                          Revisa y confirma tu correo
                        </h3>
                        <p className="text-sm text-slate-600 mt-2 max-w-sm">
                          Te enviamos los detalles de tu reserva a{" "}
                          <span className="font-semibold text-[#102542]">
                            {correo}
                          </span>
                          . Si no lo encuentras, revisa tu carpeta de spam.
                        </p>

                        {/* Precio y destino confirmados por el servidor: éste
                            es el importe que manda, no el estimado del form. */}
                        <div className="w-full mt-4 p-4 bg-slate-50 rounded-2xl border border-slate-100 flex items-center justify-between gap-3 text-left">
                          <div className="min-w-0">
                            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                              Reserva #{reserva.id} · {reserva.estado}
                            </p>
                            <p className="font-semibold text-slate-800 truncate">
                              {reserva.destino}
                            </p>
                          </div>
                          <span className="text-xl font-bold text-[#102542] shrink-0">
                            ${reserva.precio}
                          </span>
                        </div>

                        <div className="w-full mt-4 p-3 bg-amber-50 border border-amber-200 rounded-2xl flex gap-2 text-left">
                          <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                          <p className="text-xs text-amber-900 leading-relaxed">
                            <span className="font-semibold">
                              Importante:
                            </span>{" "}
                            debes confirmar la reserva desde el correo. Si no la
                            confirmas, la reservación no se hará efectiva.
                          </p>
                        </div>

                        <div className="w-full mt-6 p-4 bg-slate-50 rounded-2xl border border-slate-100">
                          <p className="text-xs text-slate-500 mb-3">
                            ¿No te llegó el correo?
                          </p>
                          <button
                            type="button"
                            onClick={handleResend}
                            disabled={resendCooldown > 0 || resending}
                            className="w-full inline-flex items-center justify-center gap-2 bg-white border border-slate-200 hover:border-[#102542] hover:bg-[#102542] hover:text-white text-[#102542] font-semibold px-5 py-3 rounded-xl transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-[#102542] disabled:hover:border-slate-200 cursor-pointer"
                          >
                            {resending ? (
                              <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Reenviando...
                              </>
                            ) : resendCooldown > 0 ? (
                              <>
                                <Clock className="w-4 h-4" />
                                Reenviar en {resendCooldown}s
                              </>
                            ) : (
                              <>
                                <RefreshCw className="w-4 h-4" />
                                Reenviar correo
                              </>
                            )}
                          </button>
                        </div>

                        <button
                          type="button"
                          onClick={onClose}
                          className="mt-5 text-sm text-slate-500 hover:text-slate-800 font-medium underline-offset-4 hover:underline cursor-pointer"
                        >
                          Cerrar ventana
                        </button>
                      </motion.div>
                    ) : (
                      <motion.form
                        key="form"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onSubmit={handleSubmit}
                        className="space-y-5"
                      >
                        {/* Resumen del viaje */}
                        <div className="bg-gradient-to-br from-slate-50 to-slate-100/50 rounded-2xl p-4 border border-slate-100">
                          <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-3">
                            Detalles del viaje
                          </p>
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div className="flex items-center gap-2">
                              <MapPin className="w-4 h-4 text-[#102542] shrink-0" />
                              <div className="min-w-0">
                                <p className="text-[10px] text-slate-500 uppercase">
                                  Destino
                                </p>
                                <p className="font-semibold text-slate-800 truncate">
                                  {resumen.destino}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-[#102542] shrink-0" />
                              <div className="min-w-0">
                                <p className="text-[10px] text-slate-500 uppercase">
                                  Fecha
                                </p>
                                <p className="font-semibold text-slate-800 truncate">
                                  {formatearFechaResumen(reservaData.fecha)}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4 text-[#102542] shrink-0" />
                              <div className="min-w-0">
                                <p className="text-[10px] text-slate-500 uppercase">
                                  Hora
                                </p>
                                <p className="font-semibold text-slate-800">
                                  {reservaData.hora}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Users className="w-4 h-4 text-[#102542] shrink-0" />
                              <div className="min-w-0">
                                <p className="text-[10px] text-slate-500 uppercase">
                                  Pasajeros
                                </p>
                                <p className="font-semibold text-slate-800">
                                  {reservaData.cant_pasajero}
                                </p>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-200">
                            <span className="text-sm font-semibold text-slate-700">
                              Total estimado
                            </span>
                            <span className="text-2xl font-bold bg-gradient-to-r from-[#102542] to-[#243d6a] bg-clip-text text-transparent">
                              ${formatearPrecio(resumen.precio)}
                            </span>
                          </div>
                        </div>

                        {/* Inputs */}
                        <div className="space-y-4">
                          <FloatingInput
                            icon={User}
                            label="Nombre completo"
                            type="text"
                            value={nombre}
                            onChange={setNombre}
                            placeholder="Juan Pérez"
                            disabled={loading}
                          />
                          <FloatingInput
                            icon={Mail}
                            label="Correo electrónico"
                            type="email"
                            value={correo}
                            onChange={setCorreo}
                            placeholder="juan@example.com"
                            disabled={loading}
                          />
                          <FloatingInput
                            icon={Phone}
                            label="Teléfono"
                            type="tel"
                            value={telefono}
                            onChange={setTelefono}
                            placeholder="+507 6000-0000"
                            disabled={loading}
                          />
                        </div>

                        {/* Disclaimer / términos */}
                        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
                          <div className="flex gap-3">
                            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                            <div className="flex-1">
                              <p className="text-sm font-semibold text-amber-900">
                                Aviso importante
                              </p>
                              <p className="text-xs text-amber-800/90 leading-relaxed mt-1">
                                Esta solicitud es referencial y queda sujeta a
                                disponibilidad y confirmación del operador. El
                                Aeropuerto Internacional de Tocumen, S.A. actúa
                                únicamente como intermediario de la información
                                y no se hace responsable por demoras, cambios
                                de tarifa, condiciones del servicio o cualquier
                                inconveniente derivado de la operación del
                                proveedor de transporte.
                              </p>
                            </div>
                          </div>
                          <label className="flex items-start gap-3 mt-3 cursor-pointer group">
                            <input
                              type="checkbox"
                              checked={aceptaTerminos}
                              onChange={(e) =>
                                setAceptaTerminos(e.target.checked)
                              }
                              disabled={loading}
                              className="mt-0.5 w-4 h-4 rounded border-amber-300 text-[#102542] focus:ring-[#102542] cursor-pointer"
                            />
                            <span className="text-xs text-amber-900 group-hover:text-amber-950 leading-relaxed">
                              He leído y acepto el aviso legal y eximo de
                              responsabilidad al aeropuerto frente a posibles
                              inconvenientes con el operador.
                            </span>
                          </label>
                        </div>

                        {/* Trust badge */}
                        <div className="flex items-center justify-center gap-2 text-xs text-slate-500">
                          <ShieldCheck className="w-4 h-4 text-emerald-500" />
                          Tus datos viajan de forma segura
                        </div>

                        {/* Botones */}
                        <div className="flex gap-3 pt-1">
                          <button
                            type="button"
                            onClick={onClose}
                            disabled={loading}
                            className="flex-1 px-5 py-3 border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 hover:border-slate-300 font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                          >
                            Cancelar
                          </button>
                          <button
                            type="submit"
                            disabled={loading || !aceptaTerminos}
                            title={
                              !aceptaTerminos
                                ? "Debes aceptar el aviso legal para continuar"
                                : undefined
                            }
                            className="flex-[1.4] px-5 py-3 bg-gradient-to-r from-[#102542] to-[#243d6a] text-white rounded-xl hover:shadow-xl hover:-translate-y-0.5 font-semibold transition-all disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0 flex items-center justify-center gap-2 cursor-pointer"
                          >
                            {loading ? (
                              <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Procesando...
                              </>
                            ) : (
                              <>
                                <Send className="w-4 h-4" />
                                Confirmar reserva
                              </>
                            )}
                          </button>
                        </div>
                      </motion.form>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

interface FloatingInputProps {
  icon: typeof User;
  label: string;
  type: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

function FloatingInput({
  icon: Icon,
  label,
  type,
  value,
  onChange,
  placeholder,
  disabled,
}: FloatingInputProps) {
  const hasValue = value.length > 0;
  return (
    <div className="relative group">
      <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
        <Icon
          className={`w-4 h-4 transition-colors ${
            hasValue ? "text-[#102542]" : "text-slate-400"
          }`}
        />
      </div>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        placeholder={placeholder}
        className="w-full pl-11 pr-4 pt-5 pb-2 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-800 focus:bg-white focus:ring-2 focus:ring-[#102542]/20 focus:border-[#102542] outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed peer placeholder-transparent"
      />
      <label
        className={`absolute left-11 transition-all duration-200 pointer-events-none text-slate-500 ${
          hasValue
            ? "top-1.5 text-[10px] uppercase tracking-wider font-semibold text-[#102542]"
            : "top-1/2 -translate-y-1/2 text-sm"
        } peer-focus:top-1.5 peer-focus:text-[10px] peer-focus:uppercase peer-focus:tracking-wider peer-focus:font-semibold peer-focus:text-[#102542] peer-focus:translate-y-0`}
      >
        {label}
      </label>
    </div>
  );
}
